// FassalkartAI - B2B Company Portal Layout

import React from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box, List, ListItemButton, ListItemIcon, ListItemText,
  Typography, Avatar, Divider, useMediaQuery, useTheme, Chip
} from '@mui/material';
import {
  Dashboard, Explore, Api, BarChart, Receipt,
  AccountCircle, MenuBook, Logout
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';

const B2B_LINKS = [
  { label: 'Dashboard', icon: <Dashboard />, path: '/b2b/dashboard' },
  { label: 'Data Explorer', icon: <Explore />, path: '/b2b/explore' },
  { label: 'API Management', icon: <Api />, path: '/b2b/api' },
  { label: 'API Docs', icon: <MenuBook />, path: '/b2b/api/docs' },
  { label: 'Reports', icon: <Receipt />, path: '/b2b/reports' },
  { label: 'Analytics', icon: <BarChart />, path: '/b2b/analytics' },
  { label: 'Subscription', icon: <Receipt />, path: '/b2b/subscription' },
  { label: 'Billing', icon: <Receipt />, path: '/b2b/billing' },
  { label: 'Company Profile', icon: <AccountCircle />, path: '/b2b/profile' },
];

const SIDEBAR_WIDTH = 260;

const B2BLayout = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();
  const { userProfile, logout } = useAuth();

  const isActive = (path) => location.pathname === path || (path !== '/b2b/dashboard' && location.pathname.startsWith(path));

  const handleLogout = async () => {
    await logout();
    navigate('/company/login');
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f8fafc' }}>
      {/* Corporate Sidebar */}
      {!isMobile && (
        <Box sx={{
          width: SIDEBAR_WIDTH, flexShrink: 0,
          bgcolor: '#0d1b2a', color: '#fff',
          display: 'flex', flexDirection: 'column',
          height: '100vh', position: 'sticky', top: 0,
          overflowY: 'auto',
        }}>
          {/* Company Logo Area */}
          <Box sx={{ p: 3, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Box component="img" src="/logo192.png" alt="FassalkartAI" sx={{ width: 28, height: 28, objectFit: 'contain' }} />
              <Typography variant="h6" fontWeight={700} sx={{ color: '#4CAF50' }}>
                Fassalkart<Box component="span" sx={{ color: '#FF8F00' }}>AI</Box>
              </Typography>
            </Box>
            <Chip label="B2B Data Portal" size="small" sx={{ bgcolor: 'rgba(76,175,80,0.2)', color: '#81c784', fontSize: 11 }} />
          </Box>

          {/* Company Profile */}
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ bgcolor: '#1565C0', width: 38, height: 38 }}>
              {userProfile?.name?.[0] || 'C'}
            </Avatar>
            <Box sx={{ overflow: 'hidden' }}>
              <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600 }} noWrap>
                {userProfile?.name || 'Company'}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                {userProfile?.subscriptionPlan || 'Starter'} Plan
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', mb: 1 }} />

          {/* Navigation */}
          <List sx={{ flex: 1, px: 1 }}>
            {B2B_LINKS.map(link => (
              <ListItemButton
                key={link.path}
                onClick={() => navigate(link.path)}
                sx={{
                  borderRadius: 2, mb: 0.5,
                  bgcolor: isActive(link.path) ? 'rgba(76,175,80,0.2)' : 'transparent',
                  color: isActive(link.path) ? '#81c784' : 'rgba(255,255,255,0.65)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' },
                }}
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>{link.icon}</ListItemIcon>
                <ListItemText primary={link.label} primaryTypographyProps={{ fontSize: 14, fontWeight: isActive(link.path) ? 600 : 400 }} />
              </ListItemButton>
            ))}
          </List>

          <Box sx={{ p: 2 }}>
            <ListItemButton onClick={handleLogout} sx={{ borderRadius: 2, color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#ef9a9a' } }}>
              <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}><Logout fontSize="small" /></ListItemIcon>
              <ListItemText primary="Logout" primaryTypographyProps={{ fontSize: 14 }} />
            </ListItemButton>
          </Box>
        </Box>
      )}

      {/* Main Content */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Top Bar */}
        <Box sx={{ height: 60, bgcolor: '#fff', borderBottom: '1px solid #e8ecf0', display: 'flex', alignItems: 'center', px: 3, position: 'sticky', top: 0, zIndex: 100 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {B2B_LINKS.find(l => isActive(l.path))?.label || 'B2B Portal'}
          </Typography>
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="caption" color="text.secondary">
              {new Date().toLocaleDateString('en-IN')}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ flex: 1, p: 3, overflowX: 'hidden' }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default B2BLayout;
